import {useCallback} from 'react';
import {NavLink, useNavigate} from 'react-router-dom';
import {useAppStore} from '@/store/appStore.ts';
import {logout} from '@/services/api';
import {Avatar, AvatarFallback} from '@/components/ui/avatar';
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover';
import {CircleDollarSign, LogOut, UserRound} from 'lucide-react';


const PAGE_MENUS = [
    {id: 'njHighlight', name: 'Performance Gap Analysis', nameZh: 'Performance Gap Analysis', route: '/nj-highlight'},
    {id: 'abnormalSales', name: 'Abnormal Sales', nameZh: '异常销售', route: '/abnormal-sales'},
    {id: 'adminBackend', name: 'Admin Backend', nameZh: '管理后台', route: '/admin-backend'},
    {id: 'adminPanel', name: 'Permission Management', nameZh: '权限管理', route: '/admin-panel'},
]


export function AppHeader() {
    const user = useAppStore((s) => s.user);

    const availableBatches = useAppStore((s) => s.availableBatches);
    const selectBatch = useAppStore((s) => s.selectBatch);
    const setSelectBatch = useAppStore((s) => s.setSelectBatch);

    const selectExchange = useAppStore((s) => s.selectExchange);
    const setSelectExchangeRate = useAppStore((s) => s.setSelectExchangeRate);

    const navigate = useNavigate();


    const userDisplay = user?.displayName || user?.email || '';
    const userMeta = ['D365', user?.email, user?.wwid ? `WWID ${user.wwid}` : ''].filter(Boolean).join(' | ');


    const handleLogout = useCallback(async () => {
        try {
            await logout();
        } catch {
            // toast handled by interceptor
        }
        navigate('/login-manual', {replace: true});
    }, [navigate]);

    return (
        <header className="app-header" style={{
            background: 'white', borderBottom: '1px solid #f1f5f9',
            padding: '12px 24px', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div style={{display: 'flex', alignItems: 'center'}}>
                    {/* Brand Logo */}
                    <div style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        color: '#eb1923',
                        fontFamily: "'JohnsonText', Arial, Helvetica, sans-serif",
                        lineHeight: 1,
                        userSelect: 'none'
                    }}>
                        <span style={{
                            fontSize: 15,
                            fontWeight: 700,
                            letterSpacing: -0.3,
                            marginBottom: 1
                        }}>Johnson&Johnson</span>
                        <span style={{
                            fontSize: 21,
                            fontWeight: 500,
                            letterSpacing: -0.6,
                            display: 'block'
                        }}>MedTech</span>
                    </div>

                    {/* Tabs as NavLinks */}
                    <nav style={{marginLeft: 24, display: 'flex', gap: 8}}>
                        {PAGE_MENUS.map((tab) => {
                            return (
                                <NavLink
                                    key={tab.id}
                                    to={tab.route}
                                    style={({isActive}: { isActive: boolean }) => ({
                                        padding: '8px 16px',
                                        border: 'none',
                                        borderRadius: 6,
                                        background: isActive ? '#e9e9e9' : 'transparent',
                                        color: isActive ? '#1a1a2e' : '#64748b',
                                        fontSize: 13,
                                        fontWeight: isActive ? 600 : 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        fontFamily: 'inherit',
                                        textDecoration: 'none',
                                    })}
                                >
                                    {tab.name}
                                </NavLink>
                            );
                        })}
                    </nav>
                </div>

                {/* Right side: batch picker + user info */}
                <div style={{display: 'flex', alignItems: 'center', gap: 16}}>
                    <span style={{fontSize: 12, color: '#718096', fontWeight: 500}}>Analysis Period:</span>
                    {selectBatch &&
                        <select
                            value={selectBatch.id}
                            onChange={(e) => setSelectBatch(e.target.value)}
                            style={{
                                fontSize: 12,
                                color: '#1a202c',
                                fontWeight: 600,
                                border: '1px solid #e2e8f0',
                                borderRadius: 4,
                                padding: '4px 8px',
                                background: 'white',
                                cursor: 'pointer',
                                outline: 'none',
                                minWidth: 70,
                                fontFamily: 'inherit',
                            }}
                        >
                            {availableBatches
                                .map((b) => <option key={b.id} value={b.id}>{b.year}-{b.month}</option>)}
                        </select>
                    }

                    <span style={{fontSize: 12, color: '#718096', fontWeight: 500}}>Currency:</span>
                    {selectExchange &&
                        <select
                            value={selectExchange.targetCurrency}
                            onChange={(e) => setSelectExchangeRate(e.target.value)}
                            style={{
                                fontSize: 12,
                                color: '#1a202c',
                                fontWeight: 600,
                                border: '1px solid #e2e8f0',
                                borderRadius: 4,
                                padding: '4px 8px',
                                background: 'white',
                                cursor: 'pointer',
                                outline: 'none',
                                minWidth: 70,
                                fontFamily: 'inherit',
                            }}
                        >
                            {/*这里暂时写死，需求不一定如何变化，当前决定只看这两个*/}
                            <option key="USD" value="USD">{"USD"}</option>
                            <option key="CNY" value="CNY">{"CNY"}</option>
                        </select>
                    }


                    <div style={{marginLeft: 8, display: 'flex', alignItems: 'center', gap: 10}}>
                        <div style={{textAlign: 'right'}}>
                            <div style={{fontSize: 12, fontWeight: 600, color: '#1a202c'}}>{userDisplay}</div>
                            {/*<div style={{fontSize: 10, color: '#94a3b8'}}>{userMeta}</div>*/}
                        </div>
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    aria-label="Open account menu"
                                    className="group rounded-full border border-transparent p-0.5 outline-none transition-all hover:border-slate-300 hover:bg-slate-50 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                                >
                                    <Avatar
                                        className="size-9 border border-slate-200 bg-white shadow-sm transition-colors group-hover:border-slate-300">
                                        <AvatarFallback
                                            className="bg-slate-100 text-slate-600 transition-colors group-hover:bg-slate-200">
                                            <UserRound className="size-4" aria-hidden="true"/>
                                        </AvatarFallback>
                                    </Avatar>
                                </button>
                            </PopoverTrigger>
                            <PopoverContent
                                align="end"
                                sideOffset={10}
                                className="p-7.5 bg-white w-[min(360px,calc(100vw-24px))] overflow-hidden rounded-lg border border-slate-200  shadow-[0_18px_45px_rgba(15,23,42,0.16)]"
                            >
                                <div style={{padding: '10px'}}
                                    className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4">
                                    <Avatar className="size-10 border border-white bg-white shadow-sm">
                                        <AvatarFallback className="bg-slate-200 text-slate-600">
                                            <UserRound className="size-4" aria-hidden="true"/>
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0 flex-1">
                                        <div
                                            className="truncate text-sm font-semibold text-slate-900">{userDisplay}</div>
                                        <div className="mt-1 truncate text-xs text-slate-500">{userMeta}</div>
                                    </div>
                                </div>

                                {/*<div style={{padding: '5px'}}>*/}
                                {/*    <div className="mb-4 flex items-center justify-between gap-4">*/}
                                {/*        <div className="flex min-w-0 items-center gap-2.5"*/}
                                {/*             style={{marginBottom: '5px'}}>*/}
                                {/*            <span*/}
                                {/*                className="flex size-8 shrink-0 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700">*/}
                                {/*                <CircleDollarSign className="size-4" aria-hidden="true"/>*/}
                                {/*            </span>*/}
                                {/*            <div className="min-w-0">*/}
                                {/*                <div className="text-sm font-semibold text-slate-900">Exchange Rate*/}
                                {/*                </div>*/}
                                {/*                <div className="mt-0.5 text-xs text-slate-500">Currency conversion*/}
                                {/*                    setting*/}
                                {/*                </div>*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*        {baseCurrency && targetCurrency && (*/}
                                {/*            <span*/}
                                {/*                className="shrink-0 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-600">*/}
                                {/*                {baseCurrency} / {targetCurrency}*/}
                                {/*            </span>*/}
                                {/*        )}*/}
                                {/*    </div>*/}

                                {/*    <div className="grid grid-cols-3 gap-2">*/}
                                {/*        <label*/}
                                {/*            className="min-w-0 rounded-md border border-slate-200 bg-white px-2.5 py-2 shadow-sm transition-colors focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-100">*/}
                                {/*            <span*/}
                                {/*                className="block text-[10px] font-semibold uppercase text-slate-400">Year</span>*/}
                                {/*            <select*/}
                                {/*                value={exchangeRateYear}*/}
                                {/*                onChange={(event) => setExchangeRateYear(event.target.value)}*/}
                                {/*                disabled={exchangeRateYears.length === 0}*/}
                                {/*                className="mt-1 h-6 w-full cursor-pointer border-0 bg-transparent p-0 text-xs font-semibold text-slate-800 outline-none disabled:cursor-not-allowed disabled:text-slate-400"*/}
                                {/*            >*/}
                                {/*                {exchangeRateYears.map((value) => (*/}
                                {/*                    <option key={value} value={value}>{value}</option>*/}
                                {/*                ))}*/}
                                {/*            </select>*/}
                                {/*        </label>*/}
                                {/*        <label*/}
                                {/*            className="min-w-0 rounded-md border border-slate-200 bg-white px-2.5 py-2 shadow-sm transition-colors focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-100">*/}
                                {/*            <span*/}
                                {/*                className="block text-[10px] font-semibold uppercase text-slate-400">Base</span>*/}
                                {/*            <select*/}
                                {/*                value={baseCurrency}*/}
                                {/*                onChange={(event) => setBaseCurrency(event.target.value)}*/}
                                {/*                disabled={baseCurrencies.length === 0}*/}
                                {/*                className="mt-1 h-6 w-full cursor-pointer border-0 bg-transparent p-0 text-xs font-semibold text-slate-800 outline-none disabled:cursor-not-allowed disabled:text-slate-400"*/}
                                {/*            >*/}
                                {/*                {baseCurrencies.map((value) => (*/}
                                {/*                    <option key={value} value={value}>{value}</option>*/}
                                {/*                ))}*/}
                                {/*            </select>*/}
                                {/*        </label>*/}
                                {/*        <label*/}
                                {/*            className="min-w-0 rounded-md border border-slate-200 bg-white px-2.5 py-2 shadow-sm transition-colors focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-100">*/}
                                {/*            <span*/}
                                {/*                className="block text-[10px] font-semibold uppercase text-slate-400">Target</span>*/}
                                {/*            <select*/}
                                {/*                value={targetCurrency}*/}
                                {/*                onChange={(event) => setTargetCurrency(event.target.value)}*/}
                                {/*                disabled={targetCurrencies.length === 0}*/}
                                {/*                className="mt-1 h-6 w-full cursor-pointer border-0 bg-transparent p-0 text-xs font-semibold text-slate-800 outline-none disabled:cursor-not-allowed disabled:text-slate-400"*/}
                                {/*            >*/}
                                {/*                {targetCurrencies.map((value) => (*/}
                                {/*                    <option key={value} value={value}>{value}</option>*/}
                                {/*                ))}*/}
                                {/*            </select>*/}
                                {/*        </label>*/}
                                {/*    </div>*/}

                                {/*    <div style={{marginTop: '5px'}}*/}
                                {/*         className="mt-3 flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3.5 py-3">*/}
                                {/*        <div>*/}
                                {/*            <div*/}
                                {/*                className="text-[10px] font-semibold uppercase text-emerald-700">Selected*/}
                                {/*                rate*/}
                                {/*            </div>*/}
                                {/*            <div className="mt-0.5 text-xs text-emerald-800">*/}
                                {/*                {baseCurrency && targetCurrency ? `1 ${baseCurrency} to ${targetCurrency}` : 'No rate available'}*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*        <span className="text-base font-semibold text-emerald-950">*/}
                                {/*            {exchangeRate === null ? '--' : exchangeRate}*/}
                                {/*        </span>*/}
                                {/*    </div>*/}
                                {/*</div>*/}

                                {/*<div style={{padding: '5px'}} className="border-t border-slate-200 bg-slate-50 p-2.5">*/}
                                {/*    <button*/}
                                {/*        type="button"*/}
                                {/*        onClick={handleLogout}*/}
                                {/*        className="flex h-9 w-full items-center gap-2.5 rounded-md border border-transparent px-3 text-sm font-medium text-red-600 transition-colors hover:border-red-100 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"*/}
                                {/*    >*/}
                                {/*        <LogOut className="size-4" aria-hidden="true"/>*/}
                                {/*        <span>Logout</span>*/}
                                {/*    </button>*/}
                                {/*</div>*/}

                            </PopoverContent>
                        </Popover>
                    </div>
                </div>
            </div>
        </header>
    );
}
